import React from "react";
import { useNavigate } from "react-router-dom";
import { BenefitsSummary } from "../utils/types";
import { Button } from "./Button";

export interface BenefitsBuilderProps {
  benefitsSummary: BenefitsSummary | null;
  useButtons?: boolean;
}

const BenefitsBuilder: React.FC<BenefitsBuilderProps> = ({
  benefitsSummary,
  useButtons = false,
}) => {
  const navigate = useNavigate();

  const handleNavigation = () => {
    if (benefitsSummary?.stage === "full") {
      navigate("/benefits-summary");
    } else if (benefitsSummary) {
      navigate("/full-benefits");
    } else {
      navigate("/getting-started");
    }
  };

  const getButtonText = () => {
    if (benefitsSummary?.stage === "full") {
      return "View details";
    } else if (benefitsSummary) {
      return benefitsSummary.extraMonthlyBenefitsAmount > 0
        ? "See what I can claim"
        : "Double-check my eligibility";
    }
    return "See if I'm owed anything";
  };

  if (benefitsSummary?.stage === "full") {
    return (
      <div
        className="flex flex-col"
        onClick={useButtons ? undefined : handleNavigation}
      >
        {benefitsSummary.monthlyBenefitsAmount === 0 ? (
          <>
            {/* User has filled out the long benefits form and is not eleigible for anything */}
            <p className="text-base my-2">
              Based on what you've shared, you're not currently eligible
              for any benefits.
            </p>
            <p className="text-base my-2">
              But even small changes in income or rent could affect your
              results.
            </p>
            <p className="text-base my-2">
              You can edit your answers anytime to see if your eligibility
              changes.
            </p>
          </>
        ) : (
          // User has filled out the long benefits form and is eligible for benefits
          <p className="text-base my-2">
            You may be missing out on up to{" "}
            <span>
              £{benefitsSummary.monthlyBenefitsAmount.toFixed(2)}
            </span>{" "}
            in monthly benefits.
          </p>
        )}
        {useButtons && (
          <>
            <hr className="my-4 border-black/10" />
            <div className="flex justify-center">
              <Button text={getButtonText()} onClick={handleNavigation} />
            </div>
          </>
        )}
      </div>
    );
  }

  if (benefitsSummary) {
    return (
      <div
        className="flex flex-col"
        onClick={useButtons ? undefined : handleNavigation}
      >
        {benefitsSummary.extraMonthlyBenefitsAmount > 0 ? (
          <>
            {/* User has filled out the short benefits form and is potentially eligible for something */}
            <p className="text-base mt-2">
              Based on what you've shared, you could be eligible for up to{" "}
              <span>
                £{benefitsSummary.extraMonthlyBenefitsAmount.toFixed(2)}
              </span>{" "}
              in monthly benefits.
            </p>
            <p className="mt-2.5 text-base">
              This is a quick estimate from the information provided.
            </p>
            <p className="mt-2.5 text-base">
              Use our free benefits calculator to see exactly which government
              benefits you could be eligible for and how to apply.
            </p>
            <p className="mt-2.5 text-base">
              It's quick, simple, and takes less than 5 minutes to complete.
            </p>
          </>
        ) : (
          <>
            {/* User has filled out the short benefits form and is not eligible, but can still check with the long form */}
            <p className="text-base mt-2">
              Based on what you've shared, it looks like you're not
              currently eligible for benefits.
            </p>
            <p className="mt-2.5 text-base">
              This is just an estimate, things can change if your situation
              does.
            </p>
            <p className="mt-2.5">
              You can still use our free benefits calculator to double-check
              your eligibility and see if any new support applies.
            </p>
            <p className="mt-2.5">
              It's quick, simple, and takes less than 5 minutes to complete.
            </p>
          </>
        )}
        {useButtons && (
          <>
            <hr className="my-4 border-black/10" />
            <div className="flex justify-center">
              <Button text={getButtonText()} onClick={handleNavigation} />
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col"
      onClick={useButtons ? undefined : handleNavigation}
    >
      {/* Default text is user has not filled out any steps e.g. skipped during onboarding */}
      <p className="text-base my-2">
        You could be missing out on government benefits. Use our free benefits
        calculator now to see what you might be entitled to. It's quick, easy,
        and could make a real difference.
      </p>
      {useButtons && (
        <>
          <hr className="my-4 border-black/10" />
          <div className="flex justify-center">
            <Button text={getButtonText()} onClick={handleNavigation} />
          </div>
        </>
      )}
    </div>
  );
};

export default BenefitsBuilder;
